import * as React from 'react';
import { pl } from 'date-fns/locale';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Button } from '@ooh/ui/components/button';
import { DateRangeButtonField } from '@ooh/ui/components/date-range-button-field';
import type { BatchReleaseMode, BatchReleaseSelectedResponse } from '@ooh/contracts';
import { formatDisplayDate } from '@ooh/ui/lib/date-format';
import { useSelectedRangeRelease } from '../api/batch-mutations';
import {
  ReleaseModeChoice,
  type ReleaseModeChoiceLabels,
} from './unified-select-release-mode-choice';
import { ReleaseSelectedResultView } from './unified-select-release-result-view';
import type { OutcomeResultUnitLookup } from './outcome-result-buckets';
import { guardClose } from './reservation-dialog-types';
import type { ReservationDialogLabels } from './reservation-dialog-types';

// ─── CancelDialog ────────────────────────────────────────────

/** The static "will be free" summary block, shown before a sub-range is picked or for
 * the whole-period choice — extracted so `CancelDialog` stays under the per-function
 * line budget. */
function CancelWillBeFreeSummary({
  label,
  mediaUnitName,
  displayDateFrom,
  displayDateTo,
}: {
  label: string;
  mediaUnitName: string;
  displayDateFrom: string;
  displayDateTo: string;
}) {
  return (
    <div className="mt-3 rounded-lg bg-muted/40 border border-border/50 px-3 py-2.5 text-xs">
      <p className="font-medium">{label}</p>
      <p className="text-muted-foreground mt-0.5">
        {mediaUnitName} · {displayDateFrom} → {displayDateTo}
      </p>
    </div>
  );
}

/**
 * CancelDialog — confirm Anuluj rezerwację, with the same whole-versus-selected-period
 * release choice `UnifiedSelectPopover`'s Zwolnij already has (CU-86cbh03pj parity,
 * reservation-panel row-action requirement). 'full' keeps the pre-existing
 * `onConfirm()` -> `onConfirmAction('cancel')` -> `useCancelReservation` path
 * unchanged; 'selected' calls the same `POST /reservations/batch-release` directly
 * (self-contained, mirroring `InspectQuickActions`) for the caller-picked sub-range,
 * then shows the result via the same `ReleaseSelectedResultView`.
 */
export function CancelDialog({
  reservationId,
  mediaUnitId,
  mediaUnitName,
  dateFrom,
  dateTo,
  labels,
  isPending = false,
  onConfirm,
  onClose,
}: {
  reservationId: number;
  mediaUnitId: number;
  mediaUnitName: string;
  dateFrom: string;
  dateTo: string;
  labels: ReservationDialogLabels;
  isPending?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [mode, setMode] = React.useState<BatchReleaseMode | null>(null);
  const [selectedFrom, setSelectedFrom] = React.useState(dateFrom);
  const [selectedTo, setSelectedTo] = React.useState(dateTo);
  const [releaseResult, setReleaseResult] = React.useState<BatchReleaseSelectedResponse | null>(
    null,
  );
  const { releaseSelectedRange, isPending: isReleasePending } = useSelectedRangeRelease();
  const busy = isPending || isReleasePending;

  const displayDateFrom = formatDisplayDate(dateFrom);
  const displayDateTo = formatDisplayDate(dateTo);
  const releaseModeChoiceLabels: ReleaseModeChoiceLabels = {
    title: labels.releaseModeChoiceTitle,
    wholeLabel: labels.releaseModeChoiceWholeLabel,
    selectedLabel: labels.releaseModeChoiceSelectedLabel,
  };

  if (releaseResult) {
    const reservationUnitLookup = new Map([[reservationId, mediaUnitId]]);
    const unitLookup: OutcomeResultUnitLookup = new Map([
      [mediaUnitId, { grwNumber: mediaUnitName, address: null, city: null }],
    ]);
    return (
      <ModalDialog
        header={<span className="text-[15px] font-semibold">{labels.cancelTitle}</span>}
        footer={null}
        onClose={onClose}
      >
        <ReleaseSelectedResultView
          response={releaseResult}
          reservationUnitLookup={reservationUnitLookup}
          unitLookup={unitLookup}
          onDismiss={onClose}
        />
      </ModalDialog>
    );
  }

  function handleConfirm() {
    if (mode === 'selected') {
      releaseSelectedRange(
        { reservationId, mediaUnitId, dateFrom: selectedFrom, dateTo: selectedTo },
        setReleaseResult,
      );
      return;
    }
    onConfirm();
  }

  return (
    <ModalDialog
      header={<span className="text-[15px] font-semibold">{labels.cancelTitle}</span>}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            {labels.cancelBtnLabel}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleConfirm}
            disabled={busy || mode === null}
          >
            {labels.cancelConfirmLabel}
          </Button>
        </div>
      }
      onClose={guardClose(onClose, busy)}
    >
      <p className="text-sm text-muted-foreground">
        {labels.cancelBody(mediaUnitName, displayDateFrom, displayDateTo)}
      </p>
      <div className="mt-3">
        <ReleaseModeChoice
          value={mode}
          onChange={(next) => {
            setMode(next);
            if (next === 'full') {
              setSelectedFrom(dateFrom);
              setSelectedTo(dateTo);
            }
          }}
          disabled={busy}
          labels={releaseModeChoiceLabels}
        />
      </div>
      {mode === 'selected' ? (
        <div className="mt-3">
          <DateRangeButtonField
            fromValue={selectedFrom}
            toValue={selectedTo}
            onChange={({ from, to }) => {
              setSelectedFrom(from);
              setSelectedTo(to);
            }}
            label={labels.dateRangeLabel}
            testId="cancel-dialog-range"
            minDate={dateFrom}
            maxDate={dateTo}
            locale={pl}
          />
        </div>
      ) : (
        <CancelWillBeFreeSummary
          label={labels.cancelWillBeFreeLabel}
          mediaUnitName={mediaUnitName}
          displayDateFrom={displayDateFrom}
          displayDateTo={displayDateTo}
        />
      )}
    </ModalDialog>
  );
}

// ─── Main export ─────────────────────────────────────────────
